import { useContext } from "react";
import { OrgContext } from "../contexts";
import { fetchDeploymentFindings } from "../api";
import { useInfiniteQuery } from "react-query";
import { calculateLookbackSeconds } from "../screens/deployment/overview/api";
import { TimeLookbackRange } from "../screens/deployment/overview/types";

type FindingsFilters = {
  dedup?: boolean;
  timeLookbackRange?: TimeLookbackRange;
  severity?: string;
  policy_name?: string;
  policy_slug?: string;
  action_type?: string;
  ref?: string;
  ref_type?: string;
  ruleset?: string;
  rule?: string;
  repo?: string;
};

let lastSince: null | number = null;
function timeLookbackRangeToSince(
  timeLookbackRange: TimeLookbackRange
): number {
  if (lastSince === null) {
    lastSince = calculateLookbackSeconds(timeLookbackRange);
  } else {
    const newSince = calculateLookbackSeconds(timeLookbackRange);
    // Set since only when the difference is higher that 1 hour, we want to prevent duplicated loading in one session
    if (Math.abs(lastSince - newSince) > 60 * 60) {
      lastSince = newSince;
    }
  }

  return lastSince;
}

export function useDeploymentFindings(filters?: FindingsFilters) {
  const { org } = useContext(OrgContext);

  const search = new URLSearchParams();
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value == null) {
        return;
      }
      if (key === "timeLookbackRange") {
        search.set(
          "since",
          timeLookbackRangeToSince(value as TimeLookbackRange).toString()
        );
      } else {
        search.set(key, String(value));
      }
    });
  }

  return useInfiniteQuery(
    // Query key search (URLSearchParams) is not serializable (https://react-query.tanstack.com/guides/query-keys))
    ["DeploymentFindings", { org, search: search.toString() }],
    ({ pageParam }) => {
      return fetchDeploymentFindings(org!, pageParam, search);
    },
    {
      enabled: !!org,
      getNextPageParam: (lastPage: any) => {
        return lastPage.fetchedNextUrl;
      },
    }
  );
}
